/**
 * Register the TaskBlock as a First-Class Page Block — F 72 MVP phase 2
 *
 * Wires the TaskBlock component into the BlockRegistryAdapter so pages
 * can host inline Task entities (via 'external' block type with
 * externalType='task').
 *
 * Usage:
 *   - Call `registerTaskBlock()` once at app boot (e.g. from main.tsx).
 *     Idempotent — safe to call multiple times.
 *   - The block surfaces in any external-block picker via the
 *     `getExternalBlockConfig('task')` lookup.
 */

import { registerExternalBlock } from '../../integration/adapters/BlockRegistryAdapter';

export const TASK_BLOCK_TYPE = 'task' as const;

export interface TaskBlockData {
  taskId: string;
}

const DEFAULT_TASK_BLOCK_DATA: TaskBlockData = { taskId: '' };

let registered = false;

export function registerTaskBlock(): void {
  if (registered) return;
  registered = true;

  registerExternalBlock({
    type: TASK_BLOCK_TYPE,
    label: 'Task',
    icon: '☑',
    renderer: 'TaskBlock',
    dataContract: {
      description: 'A Task entity rendered inline. taskId must reference an existing Task.',
      fields: [
        {
          kind: 'string',
          name: 'taskId',
          required: true,
          // no default — must be populated at insertion time via task picker
        },
      ],
    },
    defaultData: DEFAULT_TASK_BLOCK_DATA,
    category: 'interactive',
  });
}

/** Look up the block config (passes through; useful for tests). */
export function isTaskBlockRegistered(): boolean {
  return registered;
}

/** Reset registration state — test-only. */
export function _resetTaskBlockRegistration(): void {
  registered = false;
}
